import React from 'react'
import { useRouter } from 'next/router'
// Components
import Head from 'next/head'
import Image from 'next/legacy/image'
import Loader from '../../components/newLoader/Loader'
import Header from '../../components/newHeader/Header'
import CustomerReview from '../../components/search/CustomerReview'
import Footer from '../../components/newFooter/newFooter'
// Styles
import styles from './approach.module.scss'
import useMediaQuery from '../../utils/publicApi'
import { ROBOTS_META } from 'const'

export default function Approach() {
  const router = useRouter()
  const isMobile = useMediaQuery('(max-width:600px)')

  // Meta tags values
  const TITLE = 'Our Approach | MawooPets.com'
  const DESCRIPTION =
    'Our mission is to bring pets from vetted breeders to forever homes, and ensure no pet ends up in a shelter. Read our mission, and what we are doing to make it a reality.'
  const IMAGE = `${process.env.NEXT_PUBLIC_ROOT_URL}/mawoo_social_1600x906.png`
  const URL = `${process.env.NEXT_PUBLIC_ROOT_URL}/our-approach`
  const CANONICAL = `${process.env.NEXT_PUBLIC_ROOT_URL}/our-approach`

  return (
    <>
      <Head>
        {/*  Facebook tags  */}
        <meta property="og:title" content={TITLE} key="title" />
        <meta name="og:description" content={DESCRIPTION} key="description" />
        <meta property="og:type" content="website" key="type" />
        <meta property="og:url" content={URL} key="url" />
        <meta property="og:image" content={IMAGE} key="image" />
        <meta property="og:image:alt" content="Search image" key="imageAlt" />
        <meta property="og:locale" content="en_US" key="locale" />
        {/*  twitter tags  */}
        <meta
          name="twitter:card"
          content="summary_large_image"
          key="twitterCard"
        />
        <meta name="twitter:title" content={TITLE} key="twitterTitle" />
        <meta
          name="twitter:description"
          content={DESCRIPTION}
          key="twitterDescription"
        />
        <meta name="twitter:image" content={IMAGE} key="twitterImage" />
        {/*  standard tags  */}
        <title>{TITLE}</title>
        <link rel="canonical" href={CANONICAL} />
        <meta name="description" content={DESCRIPTION} />
        <meta charSet="UTF-8" />

        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, user-scalable=0"
        />
        <meta name="robots" content={ROBOTS_META} />
      </Head>
      <main className={styles.main}>
        {router.isFallback && (
          <div className={styles.loader}>
            <Loader theme="tall" />
          </div>
        )}

        <Header />
        <div className={styles.content}>
          <section className={styles.sectionHeader}>
            <div className={styles.containerHeader}>
              <div className={styles.image}>
                <Image
                  src="/mission/img/desktop/header.png"
                  alt="Lady lying on bed smiling with a brown dachshund puppy on her stomach with its tongue out"
                  layout="responsive"
                  width={1074}
                  height={330}
                  quality={100}
                  objectFit="fill"
                />
              </div>
              <div className={styles.containerTitle}>
                <h1>
                  Creating a future where every pet has a home, and every home
                  has a pet.
                </h1>
              </div>
            </div>
          </section>

          <section className={styles.sectionButtons}>
            <div className={styles.containerGrid}>
              <div className={styles.ourMission}>
                <h2 className={styles.titleMission}>Our Mission</h2>
                <a href="#mission-info" className={styles.btnMission}>
                  <img
                    src="./mission/mission.svg"
                    alt="Dog reaching up at owner\'s leg and another dog inside a small cage"
                    className={styles.iconMission}
                  />
                </a>
              </div>
              <div className={styles.theProblems}>
                <a href="#problems-info" className={styles.btnProblems}>
                  <img
                    src="./mission/problems.svg"
                    alt="Finger pointing at a dog and another dog lying down with a sign behind it"
                    className={styles.iconProblem}
                  />
                </a>
              </div>
              <div className={styles.ourSolutions}>
                <h2 className={styles.titleSolution}>Our Solutions</h2>
                <div className={styles.btnSolutions}>
                  <a href="#solution-1" className={styles.iconSolution1}>
                    {' '}
                    <img
                      src="./mission/solution_1.svg"
                      alt="Dog reading a book and smiling"
                      className={styles.images}
                    />
                  </a>
                  <a href="#solution-2" className={styles.iconSolution2}>
                    <img
                      src="./mission/solution_2.svg"
                      alt="Food bowl, medical bag, and a tennis ball"
                      className={styles.images}
                    />
                  </a>
                  <a href="#solution-3" className={styles.iconSolution3}>
                    {' '}
                    <img
                      src="./mission/solution_3.svg"
                      alt="Dog smiling with a badge next to him"
                      className={styles.images}
                    />
                  </a>
                  <a href="#solution-4" className={styles.iconSolution4}>
                    <img
                      src="./mission/solution_4.svg"
                      alt="Woman hugging a dog whose tongue is out"
                      className={styles.images}
                    />
                  </a>
                </div>
              </div>
            </div>
          </section>
          <section className={styles.sectionProblems} id="problems-info">
            <h2 className={styles.subTitles}>The Problems</h2>
            <div className={styles.containerArticle}>
              <div className={styles.aboutArticle1}>
                {!isMobile && (
                  <picture className={styles.articleImage1}>
                    <img
                      src="/mission/img/desktop/problem_1.png"
                      alt="Dog sitting and looking at the road"
                    />
                  </picture>
                )}
                <div className={styles.text1}>
                  <h3>Irresponsible Pet Parenting</h3>
                  <p>
                    Sadly, many pet owners are uninformed about the challenges
                    they will encounter while raising their pet (e.g. behavioral
                    issues and health problems). When these problems surface,
                    some owners are quick to abandon their animal companions.
                    Alarmingly, many animals are abandoned during their senior
                    and geriatric years, when they most need our help and
                    support. <br /> <br />
                    Many of these abandoned pets end up in shelters and rescues,
                    where networks of volunteers and animal lovers work their
                    best to find them new homes. However, these efforts do not
                    always succeed. According to the{' '}
                    <a
                      className={styles.links}
                      href="https://www.aspca.org/helping-people-pets/shelter-intake-and-surrender/pet-statistics#:~:text=Approximately%206.3%20million%20companion%20animals,approximately%207.2%20million%20in%202011."
                    >
                      ASPCA
                    </a>
                    , approximately 6.3 million pets enter shelters in the U.S.
                    annually. Of this population, the ASPCA estimates that 4.2
                    million find new homes, while 920,000 are euthanized. These
                    are alarming figures that beg the question, what can be done
                    to stop this problem from its root?
                  </p>
                </div>
                {isMobile && (
                  <img src="/mission/img/mobile/img_1.png" alt="img-mobile" />
                )}
              </div>
              <div className={styles.aboutArticle2}>
                <div className={styles.text2}>
                  <h3>Irresponsible Breeding</h3>
                  <p>
                    Given the ever-increasing demand for pets, the pet industry
                    has attracted many breeders who are simply looking to turn a
                    profit, and do not truly care about the health, safety or
                    well-being of their animals. Many of these unethical
                    breeders overbreed their dams (breeding moms), which is
                    emotionally stressful to the animal and detrimental to its
                    health. Overbreeding also results in an overpopulation of
                    pets that does not match the number of homes ready to accept
                    them. The result is that many pets end up in shelters.
                  </p>
                </div>
                {!isMobile ? (
                  <picture className={styles.articleImage2}>
                    <img
                      src="/mission/img/desktop/problem_2.png"
                      alt="Dog behind bars inside a cage"
                    />
                  </picture>
                ) : (
                  <img
                    src="/mission/img/mobile/img_2.png"
                    alt="Dog behind bars inside a cage"
                  />
                )}
              </div>
            </div>
          </section>
        </div>
        <section className={styles.sectionOurMission} id="mission-info">
          <div className={styles.contentMission}>
            <h2 className={styles.subTitles}>Realizing Our Mission</h2>
            <div className={styles.container}>
              <h3>Bringing Pets to Forever Homes</h3>
              <p>
                A home with pets is a home full of joy. Living with pets brings
                us happiness, purpose, a connection to nature, and the ability
                to make the world a more loving place. At Mawoo Pets, we believe
                that it is our responsibility to educate the new generation of
                pet parents and to help them become loving caretakers of their
                furry family members. <br /> <br />
                Across the world today, families are welcoming pets into their
                homes more than ever, but often it is without the knowledge or
                education to raise, nurture, and care for these animals. Having
                a pet is much like having a child in that it requires
                significant attention, care, responsibility and knowledge. A
                family that is well-informed about their pets’ needs can ensure
                that their dogs, cats and other animals live happy and healthy
                lives. Furthermore, a happy pet makes for happy family members
                who can bond with their companion through play, training and
                care. <br /> <br />
                It is for this reason that we see it as{' '}
                <span className={styles.bold}>
                  our responsibility to bring people and animals closer
                  together, and to strengthen the culture of outstanding pet
                  parenting.
                </span>
              </p>
            </div>
            <div className={styles.container}>
              <h3>No More Pets in Shelters</h3>
              <p>
                One of the biggest injustices to pets today is that many are
                mistreated, neglected, or abandoned. These animals end up in
                shelters or rescues, where the lucky ones will find new, caring
                homes. However, many are never able to find a new home. At best,
                homeless pets live their lives in shelters or foster homes. At
                worst, hauntingly, they are euthanized. <br /> <br />
                At Mawoo, we believe strongly that this problem stems from{' '}
                <span className={styles.bold}>
                  irresponsible breeding{' '}
                </span> and{' '}
                <span className={styles.bold}>careless pet parenting.</span>
              </p>
            </div>
          </div>
        </section>

        <div className={styles.content}>
          <section className={styles.sectionStepsMission}>
            <h2 className={styles.subTitles}>Our Steps Towards Our Mission</h2>
            <div className={styles.containerSolution} id="solution-3">
              <h3>Responsible Breeding</h3>
              <p>
                We believe that an effective way to discourage unethical
                breeding is to create a transparent and regulated marketplace
                that encourages and rewards responsible breeding. Such a market
                takes business away from unethical breeders and passes it to
                responsible breeders, helping diminish and hopefully eradicate
                puppy mills altogether. This is precisely our aim at Mawoo Pets.
                To achieve this, we are implementing a number of key initiatives
                across our marketplace.
              </p>
            </div>
            <div className={styles.gridArticle1}>
              <div className={styles.articleTexts1}>
                <div className={styles.text}>
                  <h3>Breeder Vetting</h3>
                  <p>
                    Every breeder on Mawoo Pets is vetted for breeding
                    frequency, health and safety standards, and socialization
                    practices. Our vetting process follows guidelines outlined
                    by the{' '}
                    <a
                      className={styles.links}
                      href="https://www.humanesociety.org/sites/default/files/docs/find-responsible-dog-breeder.pdf"
                    >
                      Humane Society of the United States
                    </a>
                    , and we actively screen against breeders that have been
                    reported for poor breeding practices by auditing bodies,
                    including the{' '}
                    <a
                      className={styles.links}
                      href="https://www.humanesociety.org/horrible-hundred"
                    >
                      Horrible Hundred
                    </a>{' '}
                    list prepared annually by the Humane Society.
                  </p>
                </div>
                <div className={styles.text}>
                  <h3>Expanded Monitoring</h3>
                  <p>
                    As Mawoo Pets grows, we are continuously expanding our
                    breeder review and auditing processes. By the first quarter
                    of 2023, we plan to conduct an in-person site visit of every
                    breeder in our network, to verify information collected in
                    their interview and onboarding process. Additionally, we are
                    instituting an audit process that will spot-check breeders
                    in our network though randomized site visits.
                  </p>
                </div>
                <div className={styles.text}>
                  <h3>Breeder Education</h3>
                  <p>
                    We believe that breeder education is one of the key enablers
                    of responsible breeding. To this end, our team is developing
                    a Breeder Education Program that will be made freely
                    available to our network of breeders. This program will
                    cover critical topics including health testing of breeding
                    parents and puppies, common illnesses in specific breeds,
                    and how to prepare a safe and comfortable space for your
                    pets.
                  </p>
                </div>
              </div>
              {!isMobile ? (
                <picture className={styles.articleImage}>
                  <img
                    src="/mission/img/desktop/mission_3.png"
                    alt="Samoyed breeding mom with her litter of puppies"
                  />
                </picture>
              ) : (
                <img src="/mission/img/mobile/img_5.png" alt="img-mobile" />
              )}
            </div>

            <div className={styles.containerSolution} id="solution-4">
              <h3>Responsible Pet Parenting</h3>
              <p className={styles.resume}>
                We believe that one of the most effective ways to stop pets from
                ending up in shelters is to educate and enable pet parents. Many
                families who bring home a new companion are not ready to do so;
                all too often, new pet parents do not know how to meet the
                challenges of caring for a growing animal. By effectively
                screening pet parents and supporting those who need help, Mawoo
                Pets will help reduce the number of pets that end up in
                shelters.
              </p>
            </div>
            <div className={styles.gridArticle2}>
              <div className={styles.articleTexts2}>
                <div className={styles.text}>
                  <h3>Customer Screening </h3>
                  <p>
                    We review information about each of our customers to ensure
                    that they bring home the right pet. Some of the main factors
                    we look at are home size, presence of children and other
                    pets, and previous experience raising pets. Additionally, we
                    scan and verify our customers’ information to stop fraud and
                    scammers, and to ensure that our pets are taken home by
                    loving and caring families.
                  </p>
                </div>
                <div className={styles.text}>
                  <h3>Lifetime Support Pledge</h3>
                  <p>
                    Mawoo Pets is available to support our customers throughout
                    their journey as a pet parent. If you ever have any
                    questions regarding the health, safety or behavior of your
                    pet, our team is here to help you find the right solution.
                    Your needs—whether they be vet care, finding a specific
                    brand of food or supplements, or networking with experienced
                    trainers and pet daycare's—are ours too! <br /> <br />
                    Furthermore,{' '}
                    <span className={styles.bold}>
                      if for any reason and at any stage of your pet’s life, you
                      are no longer able to raise your pet, our team will help
                      find him or her a new home.
                    </span>{' '}
                    We believe that every pet deserves care and protection
                    throughout its life, and ensure that no pet is abandoned.
                  </p>
                </div>
              </div>
              {!isMobile ? (
                <picture className={styles.articleImage}>
                  <img
                    src="/mission/img/desktop/mission_4.png"
                    alt="Lady sitting and petting a puppy who is leaning on her foot"
                  />
                </picture>
              ) : (
                <img src="/mission/img/mobile/img_6.png" alt="img-mobile" />
              )}
            </div>

            <div className={styles.containerSolution} id="solution-1">
              <h3>Pet Parent Education</h3>
              <p>
                We are developing training materials and educational content for
                our customers on multiple topics related to pet parenting. Our{' '}
                <a
                  className={styles.links}
                  href={`${process.env.NEXT_PUBLIC_ROOT_URL}/blog`}
                >
                  blog
                </a>{' '}
                is an excellent starting point for you and anyone else looking
                to bring home a new pet. Our articles cover subjects like
                training, health and nutrition, and some of the most common
                questions asked by new pet parents. You can find articles on
                topics ranging from the{' '}
                <a
                  className={styles.links}
                  href={`${process.env.NEXT_PUBLIC_ROOT_URL}/blog/service-dog-for-elderly`}
                >
                  Best Service Dog Breeds for the Elderly
                </a>{' '}
                to{' '}
                <a
                  className={styles.links}
                  href={`${process.env.NEXT_PUBLIC_ROOT_URL}/blog/best-dog-for-special-needs-child`}
                >
                  Choosing the Right Breed for a Special Needs Child.
                </a>
              </p>
            </div>
            <div className={styles.gridArticle1}>
              <div className={styles.articleTexts1}>
                <div className={styles.text}>
                  <h3>Breed Education</h3>
                  <p>
                    We are continuously publishing more in-depth information on
                    all standard and non-standard breeds to educate pet parents
                    on their different temperaments, health profiles, and
                    characteristics. This information is essential in selecting
                    and raising the right companion for you! Our team has
                    already developed preliminary information for 180+ breeds on
                    our Breed Pages (which include{' '}
                    <a
                      className={styles.links}
                      href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale/goldendoodle`}
                    >
                      Goldendoodle
                    </a>
                    ,{' '}
                    <a
                      className={styles.links}
                      href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale/german-shepherd-dog`}
                    >
                      German Shepherd
                    </a>
                    , and{' '}
                    <a
                      className={styles.links}
                      href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale/french-bulldog`}
                    >
                      French Bulldog
                    </a>
                    ). The pages also include information on less common mixed
                    breeds such as{' '}
                    <a
                      className={styles.links}
                      href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale/chiweenie`}
                    >
                      Chiweenies
                    </a>
                    ,{' '}
                    <a
                      className={styles.links}
                      href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale/shih-poo`}
                    >
                      Shih-Poos
                    </a>{' '}
                    and{' '}
                    <a
                      className={styles.links}
                      href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale/german-sheprador`}
                    >
                      German Shepradors
                    </a>
                    .
                  </p>
                </div>
                <div className={styles.text}>
                  <h3>Coaching</h3>
                  <p>
                    We believe that pet parents like you should have access to
                    lifelong support services and receive coaching on how to
                    best raise your pets. To this end, we are developing a
                    Coaching Program for our pet parents to receive 24/7 access
                    to experts for answers to all your questions. Our experts
                    will guide all pet parents on training, food selection,
                    common health concerns, and even reviewing genetic test
                    results.
                  </p>
                </div>
              </div>
              {!isMobile ? (
                <picture className={styles.articleImage2}>
                  <img
                    src="/mission/img/desktop/mission_1.png"
                    alt="White and black dog sitting and looking straight"
                  />
                </picture>
              ) : (
                <img src="/mission/img/mobile/img_3.png" alt="img-mobile" />
              )}
            </div>

            <div className={styles.containerSolution} id="solution-2">
              <h3>Customized Services for Pet Parents</h3>
              <p>
                As a pet parent, you need products and services that are
                tailored to the specific needs of your pet. For example, what is
                the right food for your pet? What health and genetic testing
                should your pet have done? To make this process as easy as
                possible, we are doing the hard work for you! Our team is busy
                finding the best foods, supplements, and accessories that are
                best suited to the needs of each specific breed. We’re making
                this information available to you and all of our customers, and
                we will use it ourselves to recommend the best products and
                services for your pets.
              </p>
            </div>
            <div className={styles.gridArticle2}>
              <div className={styles.articleTexts2}>
                <div className={styles.text}>
                  <h3>Personalized Food & Supplements</h3>
                  <p>
                    Mawoo Pets is developing tailored food and supplement
                    recommendations for the breed, gender, age, and genetic
                    profile of our customers' pets. These recommendations will
                    be continually updated based on the latest information
                    available from the scientific literature on the health and
                    diet of pets.
                  </p>
                </div>
                <div className={styles.text}>
                  <h3>Veterinary Connections</h3>
                  <p>
                    Mawoo Pets customers have access to our preferred network of
                    veterinarians across the U.S. and Canada. These
                    veterinarians have been vetted by our team based on their
                    qualifications, experience, expertise and customer reviews.
                    Even better, we can offer you preferred pricing on these
                    services as our customer!
                  </p>
                </div>
                <div className={styles.text}>
                  <h3>Training Connections</h3>
                  <p>
                    Similarly, Mawoo Pets customers have access to our preferred
                    network of pet trainers, all of whom are highly qualified to
                    provide behavioral training for your furry companion.
                    behavioral issues are one of the most common problems
                    encountered by new pet parents, and an experienced trainer
                    can significantly help your pet adjust to his or her new
                    home.
                  </p>
                </div>
              </div>
              {!isMobile ? (
                <picture className={styles.articleImage}>
                  <img
                    src="/mission/img/desktop/mission_2.png"
                    alt="Veterinarian inspecting a puppy"
                  />
                </picture>
              ) : (
                <img src="/mission/img/mobile/img_4.png" alt="img-mobile" />
              )}
            </div>
          </section>
        </div>
        <section className={styles.sectionPartnerWithUs}>
          <div className={styles.content}>
            <h2 className={styles.titlePartner}>Partner With Us</h2>
            <p className={styles.description}>
              Come along this journey with us to create a better world for
              animals, and to bring humans and pets closer together. We are
              seeking partners who share our vision!
            </p>
            <div className={styles.sectionGrid}>
              <div className={styles.eliminating}>
                <img
                  src="./mission/icon_eliminate.svg"
                  alt="Icon of dog inside a cage"
                  className={styles.imgIcons}
                />
                <div className={styles.containerText}>
                  <h3>Eliminating Puppy Mills</h3>
                  <p>
                    Are you aware of breeders practicing unsafe or unethical
                    breeding? If so, please{' '}
                    <a
                      href="mailto:info@mawoopets.com?subject=Partnership%20inquiry"
                      className={styles.btnChat}
                    >
                      {' '}
                      notify us{' '}
                    </a>
                    so that we can help report and shut it down!
                  </p>
                </div>
              </div>
              <div className={styles.product_ss}>
                <img
                  src="./mission/icon_pethappy.svg"
                  alt="Smiling girl with a dog sitting to her right and a heart icon above the dogʼs head"
                  className={styles.imgIcons}
                />
                <div className={styles.containerText}>
                  <h3>Products & Services for Pet Parents</h3>
                  <p>
                    Do you offer a pet insurance plan, veterinary services, pet
                    food or other products or services for pet parents?{' '}
                    <a
                      href="mailto:info@mawoopets.com?subject=Partnership%20inquiry"
                      className={styles.btnChat}
                    >
                      Contact us
                    </a>{' '}
                    to discuss how we can provide your services to our
                    customers.{' '}
                  </p>
                </div>
              </div>
              <div className={styles.responsible}>
                <img
                  src="./mission/icon_responsible.svg"
                  alt="Mawoo caring breeder certificate"
                  className={styles.imgIcons}
                />
                <div className={styles.containerText}>
                  <h3>Responsible Breeding</h3>
                  <p>
                    Do you provide education or training on responsible
                    breeding? If so, please{' '}
                    <a
                      href="mailto:info@mawoopets.com?subject=Partnership%20inquiry"
                      className={styles.btnChat}
                    >
                      contact us
                    </a>{' '}
                    to discuss how we can bring your services to our network of
                    breeders.
                  </p>
                </div>
              </div>
              <div className={styles.educating}>
                <img
                  src="./mission/icon_learn.svg"
                  alt="Icon of dog reading a book"
                  className={styles.imgIcons}
                />
                <div className={styles.containerText}>
                  <h3>Educating Pet Parents</h3>
                  <p>
                    Would you like to offer your pet service certification
                    courses, coaching for pet parents, or pet behavioral
                    training courses to Mawoo Pets’ customers?{' '}
                    <a
                      href="mailto:info@mawoopets.com?subject=Partnership%20inquiry"
                      className={styles.btnChat}
                    >
                      Contact us
                    </a>{' '}
                    to discuss the possibilities.{' '}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className={styles.componentCustomer}>
          <CustomerReview />
        </div>
        <Footer />
      </main>
    </>
  )
}
